import { logOutUser, requestRefreshToken } from '@dmp/api/api';
import { CACHE_KEYS, ROUTE_PATHS, SESSION_STORAGE_KEYS } from '@dmp/constant';
import { type CachedUserInfo, type DecodedToken, LoginMethod } from '@dmp/types';

import { cacheLoginMethod, getCachedLoginMethod } from './cache';

export function parseJwt(token: string): DecodedToken {
  try {
    // get payload from token
    const base64Url = token.split('.')[1];
    // replace URL safe characters
    const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
    // decode base64
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    throw new Error('Invalid token');
  }
}

export const logout = async () => {
  try {
    await logOutUser();
  } catch {
    // Handle errors in logout, still need to clear all frontend cache
  } finally {
    clearAllCache();
    window.location.href = ROUTE_PATHS.LANDING_PAGE;
  }
};

export const generateToken = () => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  if (!token) return null;
  return `Bearer ${token}`;
};

export const getIsLoggedIn = () => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  return !!token;
};

export const isTokenExpired = () => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  if (!token) return true;
  const decodedToken = parseJwt(token);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp < currentTime;
};

export const getCachedUserInfo = () => {
  const userInfo = localStorage.getItem(CACHE_KEYS.USER_INFO);
  return userInfo ? (JSON.parse(userInfo) as CachedUserInfo) : null;
};

export const getCachedRefreshToken = () => {
  const refreshToken = localStorage.getItem(CACHE_KEYS.REFRESH_TOKEN);
  return refreshToken;
};

export const cacheAuthInfo = (options: {
  token: string;
  refreshToken: string;
  remember_me?: boolean;
  loginMethod: LoginMethod;
}) => {
  const { token, refreshToken, remember_me, loginMethod } = options;
  localStorage.setItem(CACHE_KEYS.TOKEN, token);
  localStorage.setItem(CACHE_KEYS.REFRESH_TOKEN, refreshToken);

  const userInfo = parseJwt(token);

  localStorage.setItem(
    CACHE_KEYS.USER_INFO,
    JSON.stringify({
      ...userInfo,
      remember_me: typeof remember_me === 'boolean' ? remember_me : false,
    })
  );

  cacheLoginMethod(loginMethod);
};

export const updateUserDetails = (userDetails: {
  first_name: string;
  last_name: string;
  email: string;
  company: string;
}) => {
  let userInfo = getCachedUserInfo();
  if (userInfo) {
    userInfo = {
      ...userInfo,
      ...{
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        user_email: userDetails.email,
        company: userDetails.company,
      },
    };
    localStorage.setItem(CACHE_KEYS.USER_INFO, JSON.stringify(userInfo));
    return userInfo;
  }
  return null;
};

function clearAllCache() {
  localStorage.removeItem(CACHE_KEYS.TOKEN);
  localStorage.removeItem(CACHE_KEYS.USER_INFO);
  localStorage.removeItem(CACHE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(CACHE_KEYS.LOGIN_METHOD);

  sessionStorage.removeItem(SESSION_STORAGE_KEYS.JOURNEY_ID);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_CONFIG);
}

export const redirectToLandingPageAndClearCache = () => {
  clearAllCache();
  window.location.href = ROUTE_PATHS.LANDING_PAGE;
};

/** Cross-tab logout: the native `storage` event only fires in OTHER tabs when localStorage
 * changes, never in the tab that made the change — so this only reacts to a logout performed
 * elsewhere, and can't loop back on the tab that triggered it. Keyed off the token being removed
 * (rather than the whole `storage` event) since a token refresh also touches localStorage but
 * sets a new value instead of clearing it. */
export const subscribeToCrossTabLogout = () => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === CACHE_KEYS.TOKEN && event.newValue === null) {
      clearAllCache();
      window.location.href = ROUTE_PATHS.LANDING_PAGE;
    }
  };
  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
};

export const reLogin = () => {
  clearAllCache();
  window.location.href = ROUTE_PATHS.LOGIN;
};

export const pureRefreshToken = async () => {
  const refresh_token = getCachedRefreshToken();
  if (!refresh_token) return;
  const res = await requestRefreshToken(refresh_token);
  const userInfo = getCachedUserInfo();
  cacheAuthInfo({
    token: res.data.access_token,
    refreshToken: res.data.refresh_token,
    remember_me: userInfo?.remember_me,
    loginMethod: getCachedLoginMethod()!,
  });
};

export const checkAndRefreshToken = () => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  if (!token) return;
  const decodedToken = parseJwt(token);
  const currentTime = Date.now() / 1000;
  const isExpired = decodedToken.exp < currentTime;
  if (
    decodedToken.exp - currentTime < 60 * 60 || // 1 hour
    isExpired
  ) {
    return pureRefreshToken();
  }
};

export const getIsSsoUser = () => {
  // const userInfo = getCachedUserInfo();
  // dmp user will have { verified: true }, sso user does not.
  // token
  // 1. Centific user first login via sso.
  // extra_data: {sso: true, verified: true, sub_tenant: 'parent'}
  // 2. Centific user first register via email, then login via sso.
  // extra_data: {phone: 'xxx', sub_tenant: 'parent'}
  // 3. non-centific user register via email.
  // extra_data: {is_user_external: true, sub_tenant: 'parent', verified: true}
  // return (
  //   userInfo?.extra_data?.sso === true || // 1. Centific user first login via sso.
  //   userInfo?.extra_data?.verified === undefined // 2. Centific user first register via email, then login via sso.
  // );

  // remove the old logic, remove the dependency on token.

  const loginMethod = getCachedLoginMethod();
  return loginMethod === LoginMethod.SSO;
};

export const getIsDmpRegistrationUser = () => {
  const userInfo = getCachedUserInfo();
  return userInfo?.extra_data?.verified === true;
};

/** Whether a silent access-token refresh should be attempted on a 401 / on route change. Any
 * logged-in user with a cached refresh token is eligible — previously restricted to verified DMP
 * registration users with "remember me" checked, which silently excluded SSO users from ever being
 * refreshed and forced them straight to re-login the moment their access token expired. */
export const getShouldRefreshToken = () => {
  return !!getCachedRefreshToken();
};

export const cacheSsoEmail = (email: string) => {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.SSO_EMAIL, email);
};

export const getCachedSsoEmail = () => {
  return sessionStorage.getItem(SESSION_STORAGE_KEYS.SSO_EMAIL);
};

export const clearCachedSsoEmail = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.SSO_EMAIL);
};

export const cacheLoginRedirectPath = (path: string) => {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.LOGIN_REDIRECT_PATH, path);
};

export const getCachedLoginRedirectPath = () => {
  return sessionStorage.getItem(SESSION_STORAGE_KEYS.LOGIN_REDIRECT_PATH);
};

export const clearCachedLoginRedirectPath = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.LOGIN_REDIRECT_PATH);
};

export const redirectAfterLogin = () => {
  const redirectPath = getCachedLoginRedirectPath();
  if (redirectPath) {
    clearCachedLoginRedirectPath();
    window.location.href = redirectPath;
  } else {
    window.location.href = ROUTE_PATHS.LANDING_PAGE;
  }
};

export const cacheIsCookieSetUp = (isCookieSetUp: boolean) => {
  localStorage.setItem(CACHE_KEYS.IS_COOKIE_SET_UP, isCookieSetUp.toString());
};

export const getIsCookieSetUp = () => {
  return localStorage.getItem(CACHE_KEYS.IS_COOKIE_SET_UP) === 'true';
};

export const cacheJourneyId = (id: string) => {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.JOURNEY_ID, id);
};

export const getCachedJourneyId = () => {
  return sessionStorage.getItem(SESSION_STORAGE_KEYS.JOURNEY_ID);
};

export const clearCachedJourneyId = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.JOURNEY_ID);
};
